import React, { useEffect, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Chip, Divider, IconButton, Stack, Typography } from "@mui/material";
import { DealCreate } from "components/modals";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useCanCreateDeal, useDevice, useModals, useNavigate, usePrompt } from "hooks";
import { useReportsActions, useReportsQueriesSelector } from "store";

import { FreemiumMessages } from "../../../constants";
import { CashflowChart } from "./CashflowChart";

export const Cashflow: React.FC = () => {
  const openPrompt = usePrompt();
  const navigate = useNavigate();
  const { infoModal } = useModals();
  const { isMobile } = useDevice();
  const canCreateDeal = useCanCreateDeal();

  const { getCashflowGraphAction, getCashflowAumAction, getCashflowGraphDealsAction } = useReportsActions();
  const { currency, dateRange, exchangeMethod, filters, use_accounting_date, cashflowSetId } =
    useReportsQueriesSelector();

  const createDeal = async () => {
    const data = await openPrompt({ Component: DealCreate, props: {} });

    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  };

  const goToCashflow = () => {
    navigate({ route: "cashflow" });
    isMobile &&
      infoModal({
        title: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_TITLE,
        msg: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_MESSAGE,
        buttonText: "Continue"
      });
  };

  const {
    action: getNumberOfDeals,
    loading: numberOfDealsLoading,
    data: dealsWithData = 0
  } = useActionHook(getCashflowGraphDealsAction);

  const { action: getAumData, loading: aumLoading, data: aumData = null } = useActionHook(getCashflowAumAction);

  const { action: getGraph, data: graphData, loading: graphLoading } = useActionHook(getCashflowGraphAction);

  useEffect(() => {
    getNumberOfDeals();
  }, []);

  useEffect(() => {
    if (!cashflowSetId) return;
    getAumData({ set_id: cashflowSetId });
  }, [cashflowSetId]);

  useEffect(() => {
    if (!cashflowSetId || !aumData) return;
    getGraph({
      filters,
      currency,
      dateRange,
      exchangeDate: exchangeMethod,
      set_id: cashflowSetId,
      use_accounting_date
    });
  }, [cashflowSetId, aumData, filters, currency, dateRange, exchangeMethod, use_accounting_date]);

  const loading = graphLoading || aumLoading || numberOfDealsLoading;

  const qualityLevel = useMemo(() => {
    if (dealsWithData <= 5) return "low";
    if (dealsWithData > 5 && dealsWithData <= 10) return "medium";
    return "high";
  }, [dealsWithData]);

  const qualityIndication = {
    low: {
      label: "Low",
      color: "#EBAF01"
    },
    medium: {
      label: "Medium",
      color: "#FF9800",
      backgroundColor: "#FF9800"
    },
    high: {
      label: "High",
      color: "#07A27D",
      backgroundColor: "#07A27D"
    }
  };

  return (
    <Stack
      sx={{
        width: "100%",
        p: "16px",
        gap: "16px",
        bgcolor: "white",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px 0px #1212171A",
        maxHeight: "406px"
      }}
      alignItems="stretch"
      direction="column"
    >
      {/* Header */}
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ ...(isMobile && { gap: "10px" }) }}
      >
        <Stack direction="row" alignItems="center" gap="10px">
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#0a3142" }}>Cashflow Forecasting</Typography>
          <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={goToCashflow}>
            <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
          </IconButton>
        </Stack>
        <Stack
          direction="row"
          justifyContent={isMobile ? "space-between" : "flex-start"}
          alignItems="center"
          sx={{
            gap: isMobile ? "10px" : "20px",
            ...(isMobile && { width: "100%" })
          }}
        >
          {/* Quality indication */}
          <Stack direction="row" alignItems="center" gap="4px">
            <Typography sx={{ fontSize: "15px", fontWeight: 400, color: "#071f35", lineHeight: "20px" }}>
              Quality Indication
            </Typography>
            <MaterialTooltip isShow title="Upload more deals to improve projection accuracy" placement="top">
              <IconButton
                sx={{
                  padding: "0",
                  width: "24px",
                  height: "24px",
                  "&:hover": {
                    backgroundColor: "transparent"
                  }
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "16px", color: "#92A7BC" }} />
              </IconButton>
            </MaterialTooltip>
            <Chip
              label={qualityIndication[qualityLevel].label}
              // label={"Low" || "High"}
              sx={{
                height: "24px",
                bgcolor: qualityIndication[qualityLevel].color,
                color: "white",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
                padding: "4px",
                borderRadius: "20px",
                "& .MuiChip-label": {
                  padding: "0 4px"
                }
              }}
            />
          </Stack>
          {!isMobile && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: "24px", borderBottomWidth: "1px", backgroundColor: "#DBE8F5", borderColor: "#DBE8F5" }}
            />
          )}
          {canCreateDeal && (
            <Button
              startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
              variant="outlined"
              onClick={createDeal}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                padding: "4px 8px",
                minWidth: "auto",
                minHeight: "24px",
                height: "24px",
                borderColor: "#7b92a9",
                color: "#071f35",
                "&:hover": {
                  borderColor: "#7b92a9",
                  backgroundColor: "transparent"
                }
              }}
            >
              Add Deal
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Chart Area */}
      <CashflowChart data={graphData} aumData={aumData} loading={loading} />
    </Stack>
  );
};
